/**
 * Toast Component - Lightweight notification for non-disruptive feedback
 */
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

type ToastType = 'info' | 'success' | 'warning' | 'error';
type ToastPersona = 'default' | 'iris';

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  persona?: ToastPersona;
  speakerName?: string;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type = 'info', duration = 2000, persona = 'default', speakerName, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation
    setTimeout(() => setIsVisible(true), 10);

    // Auto close
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for fade-out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getIcon = () => {
    if (persona === 'iris') {
      return (
        <img
          src="/icons/iris-butterfly.svg"
          alt="Iris"
          style={{ width: '18px', height: '18px' }}
        />
      );
    }

    switch (type) {
      case 'success':
        return <CheckCircleIcon fontSize="small" />;
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'warning':
        return <WarningIcon fontSize="small" />;
      case 'info':
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const getColor = () => {
    switch (type) {
      case 'success':
        return '#4caf50';
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'info':
      default:
        return '#2196f3';
    }
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        backgroundColor: 'var(--node-body-background)',
        border: `1px solid ${getColor()}`,
        borderRadius: '8px',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
        zIndex: 10000,
        minWidth: '200px',
        maxWidth: '400px',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0)' : 'translateX(20px)',
        transition: 'all 0.3s ease',
        color: getColor(),
      }}
    >
      {getIcon()}
      <span style={{ 
        color: 'var(--color-text-primary)',
        fontSize: '14px',
        flex: 1
      }}>
        {speakerName && (
          <strong style={{ display: 'block', marginBottom: '2px' }}>{speakerName}</strong>
        )}
        {message}
      </span>
    </div>,
    document.body
  );
};

// Toast service for imperative usage
interface ToastOptions {
  persona?: ToastPersona;
  speakerName?: string;
}

interface ToastData {
  message: string;
  type: ToastType;
  duration?: number;
  persona?: ToastPersona;
  speakerName?: string;
}

type ToastListener = (toastData: ToastData) => void;

class ToastService {
  private listeners: ToastListener[] = [];

  show(message: string, type: ToastType = 'info', duration?: number, options?: ToastOptions): void {
    this.listeners.forEach(listener => listener({
      message,
      type,
      duration,
      persona: options?.persona,
      speakerName: options?.speakerName,
    }));
  }

  info(message: string, duration?: number, options?: ToastOptions): void {
    this.show(message, 'info', duration, options);
  }

  success(message: string, duration?: number, options?: ToastOptions): void {
    this.show(message, 'success', duration, options);
  }

  warning(message: string, duration?: number, options?: ToastOptions): void {
    this.show(message, 'warning', duration, options);
  }

  error(message: string, duration?: number, options?: ToastOptions): void {
    this.show(message, 'error', duration, options);
  }

  irisError(message: string, duration: number = 6000): void {
    this.show(message, 'error', duration, { persona: 'iris', speakerName: 'Iris' });
  }

  subscribe(listener: ToastListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }
}

export const toastService = new ToastService();
export default Toast;
